import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BuildingOfficeIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  MagnifyingGlassIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { useApi, useFormSubmit, useAsyncOperation } from '../hooks/useApi';
import { portsAPI, molesAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/helpers';
import Table from '../components/Table';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal, { ConfirmModal } from '../components/Modal';

const PortsPage = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPort, setSelectedPort] = useState(null);
  
  // Mole management state
  const [isMolesModalOpen, setIsMolesModalOpen] = useState(false);
  const [isAddMoleModalOpen, setIsAddMoleModalOpen] = useState(false);
  const [isEditMoleModalOpen, setIsEditMoleModalOpen] = useState(false);
  const [isDeleteMoleModalOpen, setIsDeleteMoleModalOpen] = useState(false);
  const [selectedMole, setSelectedMole] = useState(null);

  // API hooks
  const { 
    data: portsData, 
    loading: portsLoading, 
    refetch: refetchPorts 
  } = useApi(portsAPI.getAll);
  
  const { 
    data: managersData, 
    loading: managersLoading 
  } = useApi(() => usersAPI.getAll({ limit: 100 })); // Get all users and filter client-side

  // Moles API hook
  const { 
    data: molesData, 
    loading: molesLoading, 
    refetch: refetchMoles 
  } = useApi(() => selectedPort ? molesAPI.getByPort(selectedPort.id) : null, [selectedPort]);

  const ports = portsData?.ports || [];
  const managers = (managersData?.users || []).filter(user => 
    user.role === 'ADMIN' || user.role === 'MANAGER'
  );
  
  const accountants = (managersData?.users || []).filter(user => 
    user.role === 'ACCOUNTANT'
  );
  const moles = molesData?.moles || [];

  // Filter ports based on search
  const filteredPorts = ports.filter(port => 
    port.name.toLowerCase().includes(search.toLowerCase()) ||
    port.location.toLowerCase().includes(search.toLowerCase())
  );

  // Form submission hooks
  const { submit: submitCreate, loading: createLoading } = useFormSubmit(
    portsAPI.create,
    {
      onSuccess: () => {
        setIsAddModalOpen(false);
        refetchPorts();
      },
      successMessage: t('ports.portCreated')
    }
  );

  const { submit: submitUpdate, loading: updateLoading } = useFormSubmit(
    (data) => portsAPI.update(selectedPort.id, data),
    {
      onSuccess: () => {
        setIsEditModalOpen(false);
        setSelectedPort(null);
        refetchPorts();
      },
      successMessage: t('ports.portUpdated')
    }
  );

  const { execute: executeDelete, loading: deleteLoading } = useAsyncOperation();

  // Mole form submission hooks
  const { submit: submitCreateMole, loading: createMoleLoading } = useFormSubmit(
    molesAPI.create,
    {
      onSuccess: () => {
        setIsAddMoleModalOpen(false);
        refetchMoles();
      },
      successMessage: t('ports.moles.moleCreated')
    }
  );

  const { submit: submitUpdateMole, loading: updateMoleLoading } = useFormSubmit(
    (data) => molesAPI.update(selectedMole.id, data),
    {
      onSuccess: () => {
        setIsEditMoleModalOpen(false);
        setSelectedMole(null);
        refetchMoles();
      },
      successMessage: t('ports.moles.moleUpdated')
    }
  );

  const { execute: executeDeleteMole, loading: deleteMoleLoading } = useAsyncOperation();

  // Table columns
  const columns = [
    {
      key: 'name',
      title: t('ports.portName'),
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{row.location || t('ports.noLocationSpecified')}</div>
        </div>
      ),
    },
    {
      key: 'location',
      title: t('ports.location'),
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-700">
          {value || t('ports.noLocationSpecified')}
        </div>
      ),
    },
    {
      key: 'managers',
      title: t('ports.manager'),
      render: (value) => (
        value && value.length > 0 ? (
          <div>
            {value.map((manager, index) => (
              <div key={manager.user.id} className={index > 0 ? 'mt-1 pt-1 border-t border-gray-200' : ''}>
                <div className="text-sm text-gray-900">
                  {manager.user.firstName} {manager.user.lastName}
                </div>
                <div className="text-xs text-gray-500">@{manager.user.username}</div>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-gray-400">{t('ports.notAssigned')}</span>
        )
      ),
    },
    {
      key: 'accountants',
      title: t('ports.accountants', 'Accountants'),
      render: (value, row) => {
        // Table passes (row[column.key], row) - for 'accountants' key, first param is accountants array, second is full row
        const port = row;
        const portAccountants = port?.accountants || [];
        
        return portAccountants.length > 0 ? (
          <div>
            {portAccountants.map((accountant, index) => (
              <div key={accountant.user?.id || index} className={index > 0 ? 'mt-1 pt-1 border-t border-gray-200' : ''}>
                <div className="text-sm text-gray-900">
                  {accountant.user?.firstName} {accountant.user?.lastName}
                </div>
                <div className="text-xs text-gray-500">@{accountant.user?.username}</div>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-gray-400">{t('ports.notAssigned')}</span>
        );
      },
    },
    {
      key: 'moles',
      title: t('ports.locations'),
      align: 'center',
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {value?.length || 0}
        </span>
      ),
    },
    {
      key: 'weighbridges',
      title: t('ports.weighbridges'),
      align: 'center',
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value?.length || 0}
        </span>
      ),
    },
    {
      key: '_count',
      title: t('ports.totalLoads'),
      align: 'center',
      render: (value) => (
        <span className="text-sm font-medium text-gray-900">
          {value?.loads || 0}
        </span>
      ),
    },
    {
      key: 'status',
      title: t('ports.status'),
      align: 'center',
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'active' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {value === 'active' ? t('ports.active') : t('ports.inactive')}
        </span>
      ),
    },
    {
      key: 'createdAt',
      title: t('ports.created'),
      render: (value) => (
        <span className="text-sm text-gray-500">
          {formatDate(value)}
        </span>
      ),
    },
    {
      key: 'actions',
      title: t('ports.actions'),
      align: 'center',
      sticky: true,
      render: (_, row) => (
        <div className="flex items-center justify-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleViewMoles(row)}
            className="text-purple-600 hover:text-purple-800"
            title={t('ports.viewLocations')}
          >
            <MapPinIcon className="w-4 h-4" />
          </Button>
          {isAdmin() && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(row)}
                className="text-blue-600 hover:text-blue-800"
                title={t('ports.editPort')}
              >
                <PencilIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(row)}
                className={row._count?.loads > 0 ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-800"}
                title={row._count?.loads > 0 ? t('ports.tooltips.cannotDeleteWithLoads') : t('ports.deletePort')}
                disabled={row._count?.loads > 0}
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  // Event handlers
  const handleEdit = (port) => {
    setSelectedPort(port);
    setIsEditModalOpen(true);
  };

  const handleDelete = (port) => {
    setSelectedPort(port);
    setIsDeleteModalOpen(true);
  };

  const handleViewMoles = (port) => {
    setSelectedPort(port);
    setIsMolesModalOpen(true);
  };

  // Mole event handlers
  const handleEditMole = (mole) => {
    setSelectedMole(mole);
    setIsEditMoleModalOpen(true);
  };

  const handleDeleteMole = (mole) => {
    setSelectedMole(mole);
    setIsDeleteMoleModalOpen(true);
  };

  const confirmDelete = async () => {
    await executeDelete(
      () => portsAPI.delete(selectedPort.id),
      {
        showSuccessToast: true,
        successMessage: t('ports.portDeleted')
      }
    );
    setIsDeleteModalOpen(false);
    setSelectedPort(null);
    refetchPorts();
  };

  const confirmDeleteMole = async () => {
    await executeDeleteMole(
      () => molesAPI.delete(selectedMole.id),
      {
        showSuccessToast: true,
        successMessage: t('ports.moles.moleDeleted')
      }
    );
    setIsDeleteMoleModalOpen(false);
    setSelectedMole(null);
    refetchMoles();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('ports.title')}</h1>
            <p className="text-gray-600">{t('ports.subtitle')}</p>
          </div>
        </div>
        {isAdmin() && (
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center space-x-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span>{t('ports.addPort')}</span>
          </Button>
        )}
      </div>

      {/* Search and Controls */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder={t('ports.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <Table
          columns={columns}
          data={filteredPorts}
          loading={portsLoading}
          emptyMessage={t('ports.noPortsFound')}
        />
      </div>

      {/* Add Modal */}
      <PortForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={submitCreate}
        loading={createLoading}
        managers={managers}
        managersLoading={managersLoading}
        accountants={accountants}
        title={t('ports.addNewPort')}
        submitText={t('ports.createPort')}
      />

      {/* Edit Modal */}
      <PortForm
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedPort(null);
        }}
        onSubmit={submitUpdate}
        loading={updateLoading}
        managers={managers}
        managersLoading={managersLoading}
        accountants={accountants}
        title={t('ports.editPortTitle')}
        submitText={t('ports.updatePort')}
        initialData={selectedPort}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedPort(null);
        }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title={t('ports.deletePort')}
        message={`${t('ports.deleteConfirm')} "${selectedPort?.name}"? ${t('ports.deleteCannotUndo')}`}
        confirmText={t('common.delete')}
        variant="danger"
      />

      {/* Moles Management Modal */}
      <MolesModal
        isOpen={isMolesModalOpen}
        onClose={() => {
          setIsMolesModalOpen(false);
          setSelectedPort(null);
        }}
        port={selectedPort}
        moles={moles}
        loading={molesLoading}
        onAddMole={() => setIsAddMoleModalOpen(true)}
        onEditMole={handleEditMole}
        onDeleteMole={handleDeleteMole}
      />

              {/* Add Mole Modal */}
        <MoleForm
          isOpen={isAddMoleModalOpen}
          onClose={() => setIsAddMoleModalOpen(false)}
          onSubmit={submitCreateMole}
          loading={createMoleLoading}
          port={selectedPort}
          title={t('ports.moles.addNewLocation')}
          submitText={t('ports.moles.createLocation')}
        />

      {/* Edit Mole Modal */}
      <MoleForm
        isOpen={isEditMoleModalOpen}
        onClose={() => {
          setIsEditMoleModalOpen(false);
          setSelectedMole(null);
        }}
        onSubmit={submitUpdateMole}
        loading={updateMoleLoading}
        port={selectedPort}
        title={t('ports.moles.editLocation')}
        submitText={t('ports.moles.updateLocation')}
        initialData={selectedMole}
      />

      {/* Delete Mole Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteMoleModalOpen}
        onClose={() => {
          setIsDeleteMoleModalOpen(false);
          setSelectedMole(null);
        }}
        onConfirm={confirmDeleteMole}
        loading={deleteMoleLoading}
        title={t('ports.moles.deleteMole')}
        message={`${t('ports.deleteConfirm')} "${selectedMole?.name}"? ${t('ports.deleteCannotUndo')}`}
        confirmText={t('common.delete')}
        variant="danger"
      />
    </div>
  );
};

// Port Form Component
const PortForm = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  managers,
  managersLoading,
  accountants,
  title,
  submitText,
  initialData = null,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    managerIds: [],
    accountantIds: [],
    status: 'active',
  });

  // Update form data when modal opens with initial data
  React.useEffect(() => {
    if (isOpen) {
      if (initialData) {
        const existingManagerIds = initialData.managers?.map(m => m.user.id) || 
                                   (initialData.managerId ? [initialData.managerId] : []);
        const existingAccountantIds = initialData.accountants?.map(a => a.user.id) || [];
        setFormData({
          name: initialData.name || '',
          location: initialData.location || '',
          managerIds: existingManagerIds,
          accountantIds: existingAccountantIds,
          status: initialData.status || 'active',
        });
      } else {
        setFormData({
          name: '',
          location: '',
          managerIds: [],
          accountantIds: [],
          status: 'active',
        });
      }
    }
  }, [isOpen, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = { 
      ...formData,
      // Ensure managerIds and accountantIds are always arrays
      managerIds: formData.managerIds || [],
      accountantIds: formData.accountantIds || []
    };
    await onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleManagerChange = (managerId) => {
    setFormData(prev => {
      const currentIds = prev.managerIds || [];
      const isSelected = currentIds.includes(managerId);
      
      if (isSelected) {
        // Remove manager
        return {
          ...prev,
          managerIds: currentIds.filter(id => id !== managerId)
        };
      } else {
        // Add manager
        return {
          ...prev,
          managerIds: [...currentIds, managerId]
        };
      }
    });
  };

  const handleAccountantChange = (accountantId) => {
    setFormData(prev => {
      const currentIds = prev.accountantIds || [];
      const isSelected = currentIds.includes(accountantId);
      
      if (isSelected) {
        // Remove accountant
        return {
          ...prev,
          accountantIds: currentIds.filter(id => id !== accountantId)
        };
      } else {
        // Add accountant
        return {
          ...prev,
          accountantIds: [...currentIds, accountantId]
        };
      }
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="port-form" loading={loading}>
            {submitText}
          </Button>
        </>
      }
    >
      <form id="port-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('ports.form.portNameLabel')} *
          </label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t('ports.form.portNamePlaceholder')}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('ports.form.locationLabel')} *
          </label>
          <Input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder={t('ports.form.locationPlaceholder')}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('ports.form.managersLabel', 'Managers')}
          </label>
          {managersLoading ? (
            <div className="text-gray-500 text-sm">{t('common.loading', 'Loading...')}</div>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
              {managers.length === 0 ? (
                <div className="text-gray-500 text-sm">{t('ports.form.noManagersAvailable', 'No managers available')}</div>
              ) : (
                managers.map((manager) => (
                  <label key={manager.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.managerIds?.includes(manager.id) || false}
                      onChange={() => handleManagerChange(manager.id)}
                      className="checkbox-app"
                    />
                    <span className="text-sm text-gray-700">
                      {manager.firstName} {manager.lastName} <span className="text-gray-500">(@{manager.username})</span>
                    </span>
                  </label>
                ))
              )}
            </div>
          )}
          {formData.managerIds?.length > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {t('ports.form.selectedManagers', 'Selected managers')}: {formData.managerIds.length}
            </div>
          )}
        </div>

        {/* Accountant Assignment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('ports.form.accountantsLabel', 'Accountants')}
          </label>
          {managersLoading ? (
            <div className="text-gray-500 text-sm">{t('common.loading', 'Loading...')}</div>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
              {accountants.length === 0 ? (
                <div className="text-gray-500 text-sm">{t('ports.form.noAccountantsAvailable', 'No accountants available')}</div>
              ) : (
                accountants.map((accountant) => (
                  <label key={accountant.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.accountantIds?.includes(accountant.id) || false}
                      onChange={() => handleAccountantChange(accountant.id)}
                      className="checkbox-app"
                    />
                    <span className="text-sm text-gray-700">
                      {accountant.firstName} {accountant.lastName} <span className="text-gray-500">(@{accountant.username})</span>
                    </span>
                  </label>
                ))
              )}
            </div>
          )}
          {formData.accountantIds?.length > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {t('ports.form.selectedAccountants', 'Selected accountants')}: {formData.accountantIds.length}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('ports.form.statusLabel')}
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="input"
          >
            <option value="active">{t('ports.active')}</option>
            <option value="inactive">{t('ports.inactive')}</option>
          </select>
        </div>
      </form>
    </Modal>
  );
};

// Moles Management Modal Component
const MolesModal = ({
  isOpen,
  onClose,
  port,
  moles,
  loading,
  onAddMole,
  onEditMole,
  onDeleteMole,
}) => {
  const { t } = useTranslation();
  const molesColumns = [
    {
      key: 'name',
      title: t('ports.moles.locationName'),
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.description && (
            <div className="text-sm text-gray-500">{row.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'weighbridges',
      title: t('ports.weighbridges'),
      align: 'center',
      render: (value, row) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {row._count?.weighbridges || 0}
        </span>
      ),
    },
    {
      key: 'loads',
      title: t('ports.totalLoads'),
      align: 'center',
      render: (value, row) => (
        <span className="text-sm font-medium text-gray-900">
          {row._count?.loads || 0}
        </span>
      ),
    },
    {
      key: 'status',
      title: t('ports.status'),
      align: 'center',
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'active' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {value === 'active' ? t('ports.active') : t('ports.inactive')}
        </span>
      ),
    },
    {
      key: 'actions',
      title: t('ports.actions'),
      align: 'center',
      render: (_, row) => (
        <div className="flex items-center justify-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEditMole(row)}
            className="text-blue-600 hover:text-blue-800"
            title={t('ports.moles.editMole')}
          >
            <PencilIcon className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDeleteMole(row)}
            className="text-red-600 hover:text-red-800"
            title={t('ports.moles.deleteMole')}
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('ports.moles.title')} - ${port?.name || ''}`}
      size="xl"
      footer={
        <Button variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {t('ports.moles.manage')} {port?.name}
          </p>
          <Button
            onClick={onAddMole}
            size="sm"
            className="flex items-center space-x-2"
          >
            <PlusIcon className="w-4 h-4" />
            <span>{t('ports.moles.addMole')}</span>
          </Button>
        </div>

        <div className="border rounded-lg">
          <Table
            columns={molesColumns}
            data={moles}
            loading={loading}
            emptyMessage={t('ports.moles.noMolesFound')}
          />
        </div>
      </div>
    </Modal>
  );
};

// Mole Form Component
const MoleForm = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  port,
  title,
  submitText,
  initialData = null,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
  });

  // Update form data when modal opens with initial data
  React.useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          name: initialData.name || '',
          description: initialData.description || '',
          status: initialData.status || 'active',
        });
      } else {
        setFormData({
          name: '',
          description: '',
          status: 'active',
        });
      }
    }
  }, [isOpen, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = { 
      ...formData,
      portId: port?.id 
    };
    if (!submitData.description) {
      delete submitData.description;
    }
    await onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="mole-form" loading={loading}>
            {submitText}
          </Button>
        </>
      }
    >
      <form id="mole-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('ports.moles.form.portLabel')}
          </label>
          <Input
            type="text"
            value={port?.name || ''}
            disabled
            className="bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('ports.moles.form.locationNameLabel')} *
          </label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t('ports.moles.form.locationNamePlaceholder')}
            required
          />
          <p className="text-sm text-gray-500 mt-1">
            {t('ports.moles.form.locationNameHelp')}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('ports.moles.form.descriptionLabel')}
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder={t('ports.moles.form.descriptionPlaceholder')}
            className="input min-h-[80px] resize-none"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('ports.moles.form.statusLabel')}
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="input"
          >
            <option value="active">{t('ports.active')}</option>
            <option value="inactive">{t('ports.inactive')}</option>
          </select>
        </div>
      </form>
    </Modal>
  );
};

export default PortsPage;